import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { object, string } from 'yup'

import Button from '@/components/Button'
import Card from '@/components/Card'
import Form, { Select, Text } from '@/components/Form'
import KeyValue from '@/components/KeyValue'

import messages from './messages'

import './index.scss'

import { usePrevious } from 'react-use'

import ImagePreview from '@/components/ImagePreview'

function OrganizationOverview(props: any) {
  const intl = useIntl()

  const schema = object().shape({
    // @ts-expect-error
    defaultSpace: string().required(<FormattedMessage {...messages.requiredError} />),
    supportUrl: string()
      .trim()
      // @ts-expect-error
      .matches(/^(https?:\/\/.+|.+@.+)$/, <FormattedMessage {...messages.invalidUrl} />),
    logoURL: string()
      .trim()
      // @ts-expect-error
      .url(<FormattedMessage {...messages.invalidUrl} />)
      // @ts-expect-error
      .matches(/^$|(?:jpg|jpeg|gif|png|svg)$/, <FormattedMessage {...messages.invalidImage} />)
  })

  const [state, _setState] = useState({
    isEditing: false,
    supportUrl: props.organization.supportUrl || '',
    logoURL: props.organization.logoUrl || ''
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if (props.organization.defaultSpace !== prevProps.organization.defaultSpace) {
      setState({
        currentSpaceId: props.organization.defaultSpace.id
      })
    }
  }, [prevProps, props])

  const onCancel = () => {
    setState({ isEditing: false })
  }

  const onEdit = () => {
    setState({ isEditing: true })
  }

  const onSave = async (values: any) => {
    try {
      await props.onSaveDefaultSpace(values.defaultSpace)
      await props.onSaveOrganization({ supportUrl: values.supportUrl, logoUrl: values.logoURL })
    } finally {
      setState({
        isEditing: false
      })
    }
  }

  const onSupportUrlChange = (e: any) => {
    setState({ supportUrl: e.target.value })
  }

  const onLogoChange = (e: any) => {
    setState({ logoURL: e.target.value })
  }

  const renderReadOnly = () => {
    const { organization } = props
    return (
      <>
        <KeyValue>
          <KeyValue.Label>
            <FormattedMessage {...messages.organizationName} />
          </KeyValue.Label>
          <KeyValue.Value>{organization.name}</KeyValue.Value>
        </KeyValue>
        <KeyValue>
          <KeyValue.Label>
            <FormattedMessage {...messages.organizationId} />
          </KeyValue.Label>
          <KeyValue.Value>{organization.id}</KeyValue.Value>
        </KeyValue>
        <KeyValue>
          <KeyValue.Label>
            <FormattedMessage {...messages.companyCode} />
          </KeyValue.Label>
          <KeyValue.Value>{organization.companyCode}</KeyValue.Value>
        </KeyValue>
        {!state.isEditing && (
          <>
            <KeyValue>
              <KeyValue.Label>
                <FormattedMessage {...messages.supportUrl} />
              </KeyValue.Label>
              <KeyValue.Value>{organization.supportUrl}</KeyValue.Value>
            </KeyValue>
            <KeyValue>
              <KeyValue.Label>
                <FormattedMessage {...messages.logo} />
              </KeyValue.Label>
              <KeyValue.Value>{organization.logoUrl}</KeyValue.Value>
            </KeyValue>
            <KeyValue>
              <KeyValue.Label>
                <FormattedMessage {...messages.defaultSpace} />
              </KeyValue.Label>
              <KeyValue.Value>
                {organization.defaultSpace ? (
                  <FormattedMessage
                    {...messages.spaceId}
                    values={{
                      name: organization.defaultSpace.name,
                      id: organization.defaultSpace.id
                    }}
                  />
                ) : (
                  <FormattedMessage {...messages.none} />
                )}
              </KeyValue.Value>
            </KeyValue>
          </>
        )}
      </>
    )
  }

  return state.isEditing ? (
    <Form
      actionMessage={<FormattedMessage {...messages.saving} />}
      isActionInProgress={props.isActionInProgress}
      buttons={
        <>
          <Button color="secondary" type="reset">
            {intl.formatMessage(messages.cancelButton)}
          </Button>
          <Button color="primary" type="submit">
            {intl.formatMessage(messages.saveButton)}
          </Button>
        </>
      }
      initialValues={{
        supportUrl: props.organization.supportUrl || '',
        logoURL: props.organization.logoUrl || '',
        defaultSpace: props.organization.defaultSpace ? props.organization.defaultSpace.id : ''
      }}
      onReset={onCancel}
      onSubmit={onSave}
      title={<FormattedMessage {...messages.overview} />}
      schema={schema}
    >
      {renderReadOnly()}
      <Text
        className="supportUrl__field"
        name="supportUrl"
        label={<FormattedMessage {...messages.supportUrl} />}
        onChange={onSupportUrlChange}
      />
      <div className="org-icon-block">
        <Text
          className="logo__field"
          name="logoURL"
          label={<FormattedMessage {...messages.logo} />}
          onChange={onLogoChange}
        />
        <ImagePreview id="org-image-preview" className="logo-preview-image" imageUrl={state.logoURL} title="" />
      </div>
      <Select className="space__field" name="defaultSpace" label={<FormattedMessage {...messages.defaultSpace} />}>
        {props.spaces.map((space: any) => (
          <Select.Option key={space.id} value={space.id}>
            {space.name}
          </Select.Option>
        ))}
      </Select>
      <p className="help-description">
        <FormattedMessage {...messages.changeSpaceHelp} />
      </p>
    </Form>
  ) : (
    <Card>
      <Card.Header title={<FormattedMessage {...messages.overview} />}>
        <Button color="primary" onClick={onEdit}>
          {intl.formatMessage(messages.edit)}
        </Button>
      </Card.Header>
      <Card.Content>{renderReadOnly()}</Card.Content>
    </Card>
  )
}

OrganizationOverview.displayName = 'OrganizationOverview'

OrganizationOverview.propTypes = {
  isActionInProgress: PropTypes.bool,
  organization: PropTypes.object.isRequired,
  onSaveDefaultSpace: PropTypes.func.isRequired,
  onSaveOrganization: PropTypes.func.isRequired,
  spaces: PropTypes.arrayOf(PropTypes.object).isRequired
}

OrganizationOverview.defaultProps = {
  isActionInProgress: false
}

export default OrganizationOverview
